import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams, useSearchParams } from "react-router";

import * as Api from "@/api";
import { HELP_HOLIDAY_SETS, HELP_TIMETABLES } from "@/constants/HelpUrls";
import ContentLoader from "@/design/components/ContentLoader";
import FabBar from "@/design/components/FabBar";
import Page from "@/design/layouts/Page";
import { disableSearch, enableSearch, setHelpUrl } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viCalendar, viPlus, viTableClock } from "@sippy-platform/valkyrie";

import { Button, Grid, Tab, Tabs } from "@mui/material";
import { useHotkey } from "@tanstack/react-hotkeys";

import HolidaySetsCard from "./HolidaySetsCard";
import TimetableCard from "./TimetableCard";

function Timetables() {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tab, setTab] = useState(searchParams.get("tab") === "holidays" ? 1 : 0);

  const pbx = useSelector((state) => state.domain.pbx);
  const timetableStore = useSelector((state) => state.domain.timetables);
  const holidaySetStore = useSelector((state) => state.domain.holidaySets);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  const handleTab = (event, _tab) => setTab(_tab);

  useEffect(() => {
    dispatch(setHelpUrl(tab ? HELP_HOLIDAY_SETS : HELP_TIMETABLES));
  }, [dispatch, tab]);

  // Initializing
  useEffect(() => {
    async function getData() {
      toggleLoadingState(true);
      await Promise.all([Api.getTimetables(), Api.getHolidaySets()]).finally(toggleLoadingState);
    }

    getData();
  }, []);

  // Search every time holidaySetList or searchQuery changes
  const timetableResults = useSearch(timetableStore.list, searchValue, ["name"]);
  const holidaySetResults = useSearch(holidaySetStore.list, searchValue, ["name"]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  useHotkey("Alt+N", () =>
    navigate(tab === 0 ? `/pbx/${params.pbxId}/call-flow/timetable` : `/pbx/${params.pbxId}/call-flow/holidaysets/new`),
  );

  return (
    <Page maxWidth="xxl">
      <title>{`${tab === 0 ? "Timetables" : "Holiday sets"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Tabs value={tab} onChange={handleTab} sx={{ mt: 1, mb: 1 }}>
            <Tab label="Timetables" icon={<Valkyrie icon={viTableClock} />} />
            <Tab label="Holiday sets" icon={<Valkyrie icon={viCalendar} />} />
          </Tabs>
        </Grid>
        {tab === 0 && (
          <>
            {timetableStore.meta.count && timetableResults.length > 0 ? (
              timetableResults.map((timetable) => (
                <Grid key={timetable.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                  <TimetableCard timetable={timetable} />
                </Grid>
              ))
            ) : (
              <ContentLoader
                count={timetableStore.meta.count}
                loading={progressBar}
                noItems="You don't have any timetables yet"
                noResults="No timetables in your current query"
                fetching="Getting your timetables"
                icon={viTableClock}
              />
            )}
          </>
        )}
        {tab === 1 && (
          <>
            {holidaySetStore.meta.count && holidaySetResults.length >= 1 ? (
              holidaySetResults.map((holidaySet) => (
                <Grid key={holidaySet.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
                  <HolidaySetsCard holidaySet={holidaySet} />
                </Grid>
              ))
            ) : (
              <ContentLoader
                count={holidaySetStore.meta.count}
                loading={progressBar}
                noItems="You don't have any holiday sets yet"
                noResults="No holiday sets in your current query"
                fetching="Getting your holiday sets"
                icon={viCalendar}
              />
            )}
          </>
        )}
      </Grid>
      <FabBar>
        <Button
          variant="contained"
          component={NavLink}
          to={tab === 0 ? `../timetable` : `../holidaysets/new`}
          startIcon={<Valkyrie icon={viPlus} />}
        >
          Add
        </Button>
      </FabBar>
    </Page>
  );
}

export default Timetables;
